import { useState, useMemo } from 'react'
import { motion } from 'framer-motion'
import {
  Plus,
  Pencil,
  Trash2,
  X,
  Check,
  ArrowUp,
  ArrowDown,
  ShieldAlert,
  Layers,
  Lock,
  Users,
  AlertTriangle,
} from 'lucide-react'
import { PageHeader, fadeUp } from '@personic/shared'
import { buildBreadcrumbs } from '@/config/routes'
import {
  usePersonaEngineRoleTemplates,
  useCreateRoleTemplate,
  useUpdateRoleTemplate,
  useDeleteRoleTemplate,
} from '@/hooks/usePersonaEngine'

// =============================================================================
// TYPES
// =============================================================================

interface RoleTemplate {
  role_template_id: string
  display_name: string
  description: string
  product_context: string
  domain_directives: unknown[]
  avoidance_rules: unknown[]
  dimension_nudges: Record<string, number>
  guardrail_extensions: Record<string, unknown>
  is_active: boolean
  tenant_id: string | null
  version: number
}

interface TemplateFormData {
  role_template_id: string
  display_name: string
  description: string
  product_context: string
  domain_directives: string
  avoidance_rules: string
  dimension_nudges: string
  guardrail_extensions: string
}

// =============================================================================
// HELPERS
// =============================================================================

const EMPTY_FORM: TemplateFormData = {
  role_template_id: '',
  display_name: '',
  description: '',
  product_context: 'engage',
  domain_directives: '[]',
  avoidance_rules: '[]',
  dimension_nudges: '{}',
  guardrail_extensions: '{}',
}

function templateToForm(t: RoleTemplate): TemplateFormData {
  return {
    role_template_id: t.role_template_id,
    display_name: t.display_name,
    description: t.description,
    product_context: t.product_context,
    domain_directives: JSON.stringify(t.domain_directives, null, 2),
    avoidance_rules: JSON.stringify(t.avoidance_rules, null, 2),
    dimension_nudges: JSON.stringify(t.dimension_nudges, null, 2),
    guardrail_extensions: JSON.stringify(t.guardrail_extensions, null, 2),
  }
}

function isValidJson(str: string): boolean {
  try {
    JSON.parse(str)
    return true
  } catch {
    return false
  }
}

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

function StatusBadge({ isActive }: { isActive: boolean }) {
  return isActive ? (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700">
      <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
      Active
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-warm-100 text-warm-500">
      <span className="w-1.5 h-1.5 rounded-full bg-warm-400" />
      Inactive
    </span>
  )
}

function OwnerBadge({ tenantId }: { tenantId: string | null }) {
  return tenantId === null ? (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700">
      <Lock className="w-3 h-3" />
      System
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-sky-50 text-sky-700">
      <Users className="w-3 h-3" />
      Custom
    </span>
  )
}

function DimensionNudgePills({ nudges }: { nudges: Record<string, number> }) {
  const entries = Object.entries(nudges)
  if (entries.length === 0) {
    return <span className="text-xs text-warm-400">None</span>
  }
  return (
    <div className="flex flex-wrap gap-1">
      {entries.map(([dim, value]) => {
        const isPositive = value >= 0
        return (
          <span
            key={dim}
            className={`inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-xs font-medium ${
              isPositive
                ? 'bg-emerald-50 text-emerald-700'
                : 'bg-rose-50 text-rose-700'
            }`}
          >
            {isPositive ? (
              <ArrowUp className="w-3 h-3" />
            ) : (
              <ArrowDown className="w-3 h-3" />
            )}
            {dim}
            <span className="font-semibold ml-0.5">{value > 0 ? `+${value}` : value}</span>
          </span>
        )
      })}
    </div>
  )
}

function StatCard({ label, value, icon: Icon, color }: {
  label: string
  value: number | string
  icon: React.ElementType
  color: 'copper' | 'amber' | 'sky'
}) {
  const colorMap = {
    copper: { bg: 'bg-copper-50', icon: 'text-copper-500' },
    amber: { bg: 'bg-amber-50', icon: 'text-amber-500' },
    sky: { bg: 'bg-sky-50', icon: 'text-sky-500' },
  }
  const c = colorMap[color]
  return (
    <div className="flex items-center gap-3 p-4 rounded-xl border border-warm-200 bg-white">
      <div className={`p-2 rounded-lg ${c.bg}`}>
        <Icon className={`w-4 h-4 ${c.icon}`} />
      </div>
      <div>
        <p className="text-lg font-semibold text-midnight-800">{value}</p>
        <p className="text-xs text-warm-500">{label}</p>
      </div>
    </div>
  )
}

// =============================================================================
// INLINE FORM
// =============================================================================

function TemplateForm({
  initial,
  isCreate,
  onSubmit,
  onCancel,
  isSubmitting,
}: {
  initial: TemplateFormData
  isCreate: boolean
  onSubmit: (data: TemplateFormData) => void
  onCancel: () => void
  isSubmitting: boolean
}) {
  const [form, setForm] = useState<TemplateFormData>(initial)
  const [errors, setErrors] = useState<Partial<Record<keyof TemplateFormData, string>>>({})

  function validate(): boolean {
    const e: Partial<Record<keyof TemplateFormData, string>> = {}
    if (!form.role_template_id.trim()) e.role_template_id = 'Required'
    if (!form.display_name.trim()) e.display_name = 'Required'
    if (!form.description.trim()) e.description = 'Required'
    if (!form.product_context.trim()) e.product_context = 'Required'
    if (!isValidJson(form.domain_directives)) e.domain_directives = 'Invalid JSON'
    if (!isValidJson(form.avoidance_rules)) e.avoidance_rules = 'Invalid JSON'
    if (!isValidJson(form.dimension_nudges)) e.dimension_nudges = 'Invalid JSON'
    if (!isValidJson(form.guardrail_extensions)) e.guardrail_extensions = 'Invalid JSON'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  function handleSubmit(ev: React.FormEvent) {
    ev.preventDefault()
    if (validate()) onSubmit(form)
  }

  const update = (field: keyof TemplateFormData, value: string) =>
    setForm(prev => ({ ...prev, [field]: value }))

  return (
    <motion.form
      variants={fadeUp}
      initial="hidden"
      animate="visible"
      onSubmit={handleSubmit}
      className="rounded-xl border border-copper-200 bg-copper-50/30 p-6 mb-6"
    >
      <h3 className="font-heading font-semibold text-midnight-800 mb-4">
        {isCreate ? 'Create Role Template' : 'Edit Role Template'}
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        {/* Role Template ID */}
        <div>
          <label className="block text-xs font-medium text-warm-600 mb-1">Role Template ID</label>
          <input
            type="text"
            value={form.role_template_id}
            onChange={e => update('role_template_id', e.target.value)}
            disabled={!isCreate}
            placeholder="e.g. sales_closer"
            className={`input w-full ${!isCreate ? 'opacity-60 cursor-not-allowed' : ''}`}
          />
          {errors.role_template_id && (
            <p className="text-xs text-red-500 mt-1">{errors.role_template_id}</p>
          )}
        </div>

        {/* Display Name */}
        <div>
          <label className="block text-xs font-medium text-warm-600 mb-1">Display Name</label>
          <input
            type="text"
            value={form.display_name}
            onChange={e => update('display_name', e.target.value)}
            placeholder="e.g. Sales Closer"
            className="input w-full"
          />
          {errors.display_name && (
            <p className="text-xs text-red-500 mt-1">{errors.display_name}</p>
          )}
        </div>

        {/* Product Context */}
        <div>
          <label className="block text-xs font-medium text-warm-600 mb-1">Product Context</label>
          <select
            value={form.product_context}
            onChange={e => update('product_context', e.target.value)}
            className="input w-full"
          >
            <option value="engage">engage</option>
            <option value="pragmatic">pragmatic</option>
            <option value="consumer">consumer</option>
            <option value="extension">extension</option>
          </select>
          {errors.product_context && (
            <p className="text-xs text-red-500 mt-1">{errors.product_context}</p>
          )}
        </div>

        {/* Description */}
        <div>
          <label className="block text-xs font-medium text-warm-600 mb-1">Description</label>
          <input
            type="text"
            value={form.description}
            onChange={e => update('description', e.target.value)}
            placeholder="Brief description of the role template"
            className="input w-full"
          />
          {errors.description && (
            <p className="text-xs text-red-500 mt-1">{errors.description}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        {/* Domain Directives */}
        <div>
          <label className="block text-xs font-medium text-warm-600 mb-1">Domain Directives (JSON array)</label>
          <textarea
            value={form.domain_directives}
            onChange={e => update('domain_directives', e.target.value)}
            rows={3}
            className="input w-full font-mono text-xs"
          />
          {errors.domain_directives && (
            <p className="text-xs text-red-500 mt-1">{errors.domain_directives}</p>
          )}
        </div>

        {/* Avoidance Rules */}
        <div>
          <label className="block text-xs font-medium text-warm-600 mb-1">Avoidance Rules (JSON array)</label>
          <textarea
            value={form.avoidance_rules}
            onChange={e => update('avoidance_rules', e.target.value)}
            rows={3}
            className="input w-full font-mono text-xs"
          />
          {errors.avoidance_rules && (
            <p className="text-xs text-red-500 mt-1">{errors.avoidance_rules}</p>
          )}
        </div>

        {/* Dimension Nudges */}
        <div>
          <label className="block text-xs font-medium text-warm-600 mb-1">Dimension Nudges (JSON object)</label>
          <textarea
            value={form.dimension_nudges}
            onChange={e => update('dimension_nudges', e.target.value)}
            rows={3}
            className="input w-full font-mono text-xs"
            placeholder='{"assertiveness": 0.2, "empathy": -0.1}'
          />
          {errors.dimension_nudges && (
            <p className="text-xs text-red-500 mt-1">{errors.dimension_nudges}</p>
          )}
        </div>

        {/* Guardrail Extensions */}
        <div>
          <label className="block text-xs font-medium text-warm-600 mb-1">Guardrail Extensions (JSON object)</label>
          <textarea
            value={form.guardrail_extensions}
            onChange={e => update('guardrail_extensions', e.target.value)}
            rows={3}
            className="input w-full font-mono text-xs"
          />
          {errors.guardrail_extensions && (
            <p className="text-xs text-red-500 mt-1">{errors.guardrail_extensions}</p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button
          type="submit"
          disabled={isSubmitting}
          className="btn btn-primary inline-flex items-center gap-2"
        >
          <Check className="w-4 h-4" />
          {isSubmitting ? 'Saving...' : isCreate ? 'Create Template' : 'Save Changes'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="btn btn-secondary inline-flex items-center gap-2"
        >
          <X className="w-4 h-4" />
          Cancel
        </button>
      </div>
    </motion.form>
  )
}

// =============================================================================
// DELETE CONFIRMATION
// =============================================================================

function DeleteConfirmation({
  template,
  onConfirm,
  onCancel,
  isDeleting,
}: {
  template: RoleTemplate
  onConfirm: () => void
  onCancel: () => void
  isDeleting: boolean
}) {
  const isSystem = template.tenant_id === null

  return (
    <motion.div
      variants={fadeUp}
      initial="hidden"
      animate="visible"
      className="rounded-xl border border-red-200 bg-red-50/50 p-5 mb-4"
    >
      {isSystem ? (
        <div className="flex items-start gap-3">
          <ShieldAlert className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-red-800 mb-1">Cannot delete system templates</p>
            <p className="text-xs text-red-600">
              "{template.display_name}" is a system-level template (tenant_id is null) and cannot be
              deleted. Only custom tenant templates may be removed.
            </p>
            <button
              onClick={onCancel}
              className="mt-3 btn btn-secondary text-xs inline-flex items-center gap-1"
            >
              <X className="w-3.5 h-3.5" />
              Dismiss
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-red-800 mb-1">
              Delete "{template.display_name}"?
            </p>
            <p className="text-xs text-red-600 mb-3">
              This action is permanent. All engage sessions referencing this template will fall back
              to the default role.
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={onConfirm}
                disabled={isDeleting}
                className="btn bg-red-600 text-white hover:bg-red-700 text-xs inline-flex items-center gap-1"
              >
                <Trash2 className="w-3.5 h-3.5" />
                {isDeleting ? 'Deleting...' : 'Delete Template'}
              </button>
              <button
                onClick={onCancel}
                disabled={isDeleting}
                className="btn btn-secondary text-xs inline-flex items-center gap-1"
              >
                <X className="w-3.5 h-3.5" />
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  )
}

// =============================================================================
// SKELETON ROW
// =============================================================================

function SkeletonRow() {
  return (
    <tr>
      {Array.from({ length: 6 }).map((_, j) => (
        <td key={j} className="px-5 py-4">
          <div
            className="h-4 bg-warm-100 rounded animate-pulse"
            style={{ width: `${50 + Math.random() * 40}%` }}
          />
        </td>
      ))}
    </tr>
  )
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function RoleTemplateManager() {
  const breadcrumbs = buildBreadcrumbs('/admin/persona-engine/roles')

  // Data
  const { data: templates, isLoading } = usePersonaEngineRoleTemplates()
  const createMutation = useCreateRoleTemplate()
  const updateMutation = useUpdateRoleTemplate()
  const deleteMutation = useDeleteRoleTemplate()

  // UI state
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [deletingTemplate, setDeletingTemplate] = useState<RoleTemplate | null>(null)

  // Summary stats
  const summary = useMemo(() => {
    if (!templates) return { total: 0, system: 0, custom: 0 }
    return {
      total: templates.length,
      system: templates.filter(t => t.tenant_id === null).length,
      custom: templates.filter(t => t.tenant_id !== null).length,
    }
  }, [templates])

  // Handlers
  function handleCreate(data: TemplateFormData) {
    createMutation.mutate(
      {
        role_template_id: data.role_template_id,
        display_name: data.display_name,
        description: data.description,
        product_context: data.product_context,
        domain_directives: JSON.parse(data.domain_directives),
        avoidance_rules: JSON.parse(data.avoidance_rules),
        dimension_nudges: JSON.parse(data.dimension_nudges),
        guardrail_extensions: JSON.parse(data.guardrail_extensions),
      },
      {
        onSuccess: () => setShowCreateForm(false),
      },
    )
  }

  function handleUpdate(data: TemplateFormData) {
    updateMutation.mutate(
      {
        id: data.role_template_id,
        display_name: data.display_name,
        description: data.description,
        domain_directives: JSON.parse(data.domain_directives),
        avoidance_rules: JSON.parse(data.avoidance_rules),
        dimension_nudges: JSON.parse(data.dimension_nudges),
        guardrail_extensions: JSON.parse(data.guardrail_extensions),
      },
      {
        onSuccess: () => setEditingId(null),
      },
    )
  }

  function handleDeleteClick(template: RoleTemplate) {
    setDeletingTemplate(template)
  }

  function handleDeleteConfirm() {
    if (!deletingTemplate) return
    deleteMutation.mutate(deletingTemplate.role_template_id, {
      onSuccess: () => setDeletingTemplate(null),
    })
  }

  return (
    <div>
      <PageHeader
        title="Role Template Manager"
        description="Manage Engage B2B role templates that shape persona behavior through dimension nudges, domain directives, and guardrails."
        breadcrumbs={breadcrumbs}
        action={
          !showCreateForm ? (
            <button
              onClick={() => {
                setShowCreateForm(true)
                setEditingId(null)
              }}
              className="btn btn-primary inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Create Template
            </button>
          ) : undefined
        }
      />

      {/* Summary Cards */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        animate="visible"
        className="grid grid-cols-3 gap-3 mb-6"
      >
        <StatCard
          label="Total Templates"
          value={isLoading ? '--' : summary.total}
          icon={Layers}
          color="copper"
        />
        <StatCard
          label="System Templates"
          value={isLoading ? '--' : summary.system}
          icon={Lock}
          color="amber"
        />
        <StatCard
          label="Custom Templates"
          value={isLoading ? '--' : summary.custom}
          icon={Users}
          color="sky"
        />
      </motion.div>

      {/* Create Form (inline) */}
      {showCreateForm && (
        <TemplateForm
          initial={EMPTY_FORM}
          isCreate
          onSubmit={handleCreate}
          onCancel={() => setShowCreateForm(false)}
          isSubmitting={createMutation.isPending}
        />
      )}

      {/* Delete Confirmation */}
      {deletingTemplate && (
        <DeleteConfirmation
          template={deletingTemplate}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeletingTemplate(null)}
          isDeleting={deleteMutation.isPending}
        />
      )}

      {/* Table */}
      <motion.div variants={fadeUp} initial="hidden" animate="visible">
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-warm-200">
              <thead className="bg-warm-50/60">
                <tr>
                  {['Display Name', 'Template ID', 'Product Context', 'Dimension Nudges', 'Status', 'Actions'].map(
                    col => (
                      <th
                        key={col}
                        className="px-5 py-3.5 text-left text-xs font-semibold text-warm-500 uppercase tracking-wider"
                      >
                        {col}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-warm-100 bg-white">
                {isLoading ? (
                  Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} />)
                ) : !templates || templates.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-5 py-16 text-center">
                      <Layers className="w-10 h-10 text-warm-300 mx-auto mb-3" />
                      <p className="text-warm-500 font-medium">No role templates found</p>
                      <p className="text-warm-400 text-sm mt-1">
                        Create your first template to get started.
                      </p>
                    </td>
                  </tr>
                ) : (
                  templates.map(t => {
                    const isEditing = editingId === t.role_template_id

                    if (isEditing) {
                      return (
                        <tr key={t.role_template_id}>
                          <td colSpan={6} className="p-0">
                            <div className="p-4">
                              <TemplateForm
                                initial={templateToForm(t)}
                                isCreate={false}
                                onSubmit={handleUpdate}
                                onCancel={() => setEditingId(null)}
                                isSubmitting={updateMutation.isPending}
                              />
                            </div>
                          </td>
                        </tr>
                      )
                    }

                    return (
                      <tr
                        key={t.role_template_id}
                        className="hover:bg-copper-50/30 transition-colors"
                      >
                        <td className="px-5 py-4">
                          <div>
                            <p className="text-sm font-medium text-midnight-800">
                              {t.display_name}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <OwnerBadge tenantId={t.tenant_id} />
                              <span className="text-xs text-warm-400">v{t.version}</span>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <code className="text-xs bg-warm-100 text-warm-700 px-2 py-1 rounded font-mono">
                            {t.role_template_id}
                          </code>
                        </td>
                        <td className="px-5 py-4">
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-warm-100 text-warm-700">
                            {t.product_context}
                          </span>
                        </td>
                        <td className="px-5 py-4">
                          <DimensionNudgePills nudges={t.dimension_nudges} />
                        </td>
                        <td className="px-5 py-4">
                          <StatusBadge isActive={t.is_active} />
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => {
                                setEditingId(t.role_template_id)
                                setShowCreateForm(false)
                                setDeletingTemplate(null)
                              }}
                              className="p-1.5 rounded-lg hover:bg-warm-100 transition-colors"
                              title="Edit template"
                            >
                              <Pencil className="w-4 h-4 text-warm-500 hover:text-copper-600" />
                            </button>
                            <button
                              onClick={() => handleDeleteClick(t)}
                              className="p-1.5 rounded-lg hover:bg-red-50 transition-colors"
                              title="Delete template"
                            >
                              <Trash2 className="w-4 h-4 text-warm-400 hover:text-red-500" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </motion.div>
    </div>
  )
}
